// FILE: src/pages/dealer/DealerListingDetail.jsx
// UPDATED: Shows BiddingPassModal when dealer has no pass and tries to bid

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useDealerBidding from '../../hooks/useDealerBidding';
import { dealerAPI } from '../../services/dealerAPI';
import BiddingPassModal from '../../components/dealer/BiddingPassModal';

const fmt   = (val) => val ? `₹${Number(val).toLocaleString('en-IN')}` : '—';
const fmtKm = (v)   => v ? `${Number(v).toLocaleString('en-IN')} km` : '—';

const isBiddingClosed = (l) =>
  !l ? false :
  l.dealer_status  === 'deal_finalized' ||
  l.bidding_status === 'closed'         ||
  l.deal_closed_at != null              ||
  (l.bidding_end_at && new Date(l.bidding_end_at) < new Date());

const scoreColor = (s) => s >= 8 ? '#16a34a' : s >= 6 ? '#d97706' : '#dc2626';

const itemStatusStyle = (s) => {
  const m = {
    good: ['#dcfce7','#166534'], fair: ['#fef9c3','#854d0e'],
    poor: ['#fee2e2','#991b1b'], not_checked: ['#f3f4f6','#6b7280'],
  };
  return m[s] || m.not_checked;
};

const Countdown = ({ endAt }) => {
  const [rem, setRem] = useState('');
  useEffect(() => {
    if (!endAt) return;
    const tick = () => {
      const ms = new Date(endAt) - Date.now();
      if (ms <= 0) { setRem('Ended'); return; }
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
      setRem(h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [endAt]);
  const urgent = endAt && (new Date(endAt) - Date.now()) < 3600000;
  return (
    <span style={{ fontFamily:'monospace', fontWeight:800, fontSize:13, color:urgent?'#dc2626':'#16a34a', background:urgent?'#fee2e2':'#dcfce7', padding:'3px 10px', borderRadius:20 }}>
      ⏱ {rem}
    </span>
  );
};

const ScoreRing = ({ label, score, max = 10 }) => {
  const pct = Math.round((Number(score) / max) * 100);
  const c = scoreColor(max === 100 ? score / 10 : score);
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
      <div style={{ width:60, height:60, borderRadius:'50%', background:`conic-gradient(${c} ${pct}%, #e5e7eb ${pct}%)`, display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
        <div style={{ width:42, height:42, borderRadius:'50%', background:'#fff', position:'absolute' }}/>
        <span style={{ position:'relative', zIndex:1, fontSize:11, fontWeight:800, color:'#003566' }}>{Number(score).toFixed(1)}</span>
      </div>
      <span style={{ fontSize:9, color:'#6b7280', textAlign:'center', fontWeight:700, textTransform:'uppercase', letterSpacing:0.5, lineHeight:1.3, maxWidth:60 }}>{label}</span>
    </div>
  );
};

export default function DealerListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [listing,       setListing]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [activeImg,     setActiveImg]     = useState(0);
  const [offerPrice,    setOfferPrice]    = useState('');
  const [placing,       setPlacing]       = useState(false);
  const [toast,         setToast]         = useState(null);
  const [connected,     setConnected]     = useState(false);
  const [liveStats,     setLiveStats]     = useState({ highest: null, total: 0 });
  const [liveFlash,     setLiveFlash]     = useState(false);
  const [isClosed,      setIsClosed]      = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [hasPass,       setHasPass]       = useState(false);
  const [passLoading,   setPassLoading]   = useState(true);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type }); setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchListing = useCallback(async () => {
    try {
      setLoading(true);
      const res = await dealerAPI.getListingDetail(id);
      const l = res.data?.data || res.data;
      setListing(l);
      setLiveStats({ highest: l.highest_offer_price, total: l.total_offers_count || 0 });
      setIsClosed(isBiddingClosed(l));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load listing.');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchListing(); }, [fetchListing]);

  // Check bidding pass
  useEffect(() => {
    setPassLoading(true);
    dealerAPI.getBiddingPassPlans()
      .then(res => setHasPass(res.data?.has_access || false))
      .catch(() => setHasPass(false))
      .finally(() => setPassLoading(false));
  }, []);

  useDealerBidding(id, {
    onConnected:    () => setConnected(true),
    onDisconnected: () => setConnected(false),
    onNewBid: (p) => {
      if (isClosed) return;
      setLiveStats({ highest: p.highest_price, total: p.total_offers });
      setLiveFlash(true); setTimeout(() => setLiveFlash(false), 1200);
    },
    onBidResult: () => {
      showToast('Deal finalized by admin!');
      setIsClosed(true); fetchListing();
    },
  });

  const handlePlaceBid = async () => {
    // ── Check pass first ──
    if (!hasPass) {
      setShowPassModal(true);
      return;
    }
    const price = Number(offerPrice);
    if (!price || price <= 0) { showToast('Enter a valid amount.', 'error'); return; }
    if (liveStats.highest && price <= Number(liveStats.highest)) {
      showToast(`Must exceed current highest: ${fmt(liveStats.highest)}`, 'error'); return;
    }
    try {
      setPlacing(true);
      await dealerAPI.placeBid(id, price);
      showToast('Bid placed successfully! 🎉'); setOfferPrice('');
      setTimeout(fetchListing, 1500);
    } catch (err) {
      const data = err.response?.data;
      if (data?.needs_pass) {
        setHasPass(false);
        setShowPassModal(true);
      } else {
        showToast(data?.error || 'Failed to place bid.', 'error');
      }
    } finally { setPlacing(false); }
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', flexDirection:'column', gap:16 }}>
      <div style={{ width:48, height:48, border:'5px solid #e0e0e0', borderTopColor:'#003566', borderRadius:'50%', animation:'spin 0.9s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <p style={{ color:'#666', fontWeight:600 }}>Loading listing…</p>
    </div>
  );
  if (error) return (
    <div style={{ padding:40, textAlign:'center' }}>
      <p style={{ color:'#dc2626', fontSize:16, fontWeight:700 }}>⚠️ {error}</p>
      <button onClick={() => navigate(-1)} style={{ marginTop:16, padding:'10px 24px', background:'#003566', color:'#FFD60A', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700 }}>← Go Back</button>
    </div>
  );

  const car    = listing?.car || {};
  const report = listing?.inspection_report;
  const items  = listing?.inspection_items || [];
  const images = listing?.images || [];
  const itemsByCategory = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item); return acc;
  }, {});

  return (
    <div style={{ padding:'24px', maxWidth:1100, margin:'0 auto' }}>
      <style>{`
        @keyframes spin    { to { transform:rotate(360deg); } }
        @keyframes pulse   { 0%,100%{box-shadow:0 0 0 4px #28a74530}50%{box-shadow:0 0 0 8px #28a74510} }
        @keyframes flash   { 0%{background:#FFD60A30}100%{background:transparent} }
        @keyframes slideIn { from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)} }
      `}</style>

      {toast && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:9999, background:toast.type==='success'?'#16a34a':'#dc2626', color:'#fff', padding:'13px 22px', borderRadius:10, fontWeight:700, fontSize:14, boxShadow:'0 4px 20px rgba(0,0,0,0.25)', animation:'slideIn 0.3s ease' }}>
          {toast.type==='success'?'✅ ':'❌ '}{toast.msg}
        </div>
      )}

      <button onClick={() => navigate('/dealer/listings')} style={{ background:'none', border:'none', color:'#003566', fontWeight:700, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', gap:6, padding:0, marginBottom:20 }}>
        ← Back to Listings
      </button>

      {/* HERO */}
      <div style={{ background:'linear-gradient(135deg,#003566,#001d3d)', borderRadius:16, padding:'22px 28px', marginBottom:24, border:isClosed?'2px solid #dc354560':'2px solid #FFD60A' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
          <div>
            <h1 style={{ margin:'0 0 6px', color:'#FFD60A', fontSize:26, fontWeight:800 }}>
              {car.brand} {car.model} {car.year}
            </h1>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:14 }}>
              {car.city     && <span style={{ color:'rgba(255,255,255,0.75)', fontSize:13 }}>📍 {car.city}</span>}
              {car.km_driven && <span style={{ color:'rgba(255,255,255,0.75)', fontSize:13 }}>🛣️ {fmtKm(car.km_driven)}</span>}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <span style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:700, color:isClosed?'#dc2626':connected?'#16a34a':'#d97706' }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:isClosed?'#dc2626':connected?'#16a34a':'#d97706', animation:(!isClosed&&connected)?'pulse 1.5s infinite':'none', display:'inline-block' }}/>
                {isClosed ? 'Bidding Closed' : connected ? 'Live Bidding Active' : 'Connecting…'}
              </span>
              {!isClosed && listing?.bidding_end_at && <Countdown endAt={listing.bidding_end_at} />}
            </div>
          </div>
          <div style={{ background:isClosed?'rgba(220,53,69,0.15)':'rgba(255,214,10,0.1)', border:`1px solid ${isClosed?'rgba(220,53,69,0.4)':'rgba(255,214,10,0.4)'}`, borderRadius:12, padding:'14px 20px', textAlign:'center', minWidth:140 }}>
            <p style={{ margin:'0 0 4px', fontSize:10, fontWeight:700, color:isClosed?'#dc2626':'rgba(255,214,10,0.85)', textTransform:'uppercase', letterSpacing:0.8 }}>
              {isClosed ? 'Final Bid' : 'Current Highest'}
            </p>
            <p style={{ margin:0, fontSize:24, fontWeight:800, color:isClosed?'#dc2626':'#FFD60A' }}>
              {fmt(liveStats.highest) || '—'}
            </p>
            <p style={{ margin:'3px 0 0', fontSize:12, color:'rgba(255,255,255,0.55)' }}>
              {liveStats.total} bid{liveStats.total !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* TWO-COLUMN */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:24, alignItems:'start' }}>
        {/* LEFT */}
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          {/* Images */}
          {images.length > 0 ? (
            <div style={{ background:'#fff', borderRadius:16, overflow:'hidden', boxShadow:'0 2px 12px rgba(0,53,102,0.08)' }}>
              <div style={{ height:280, background:'linear-gradient(135deg,#001f3f,#003566)', overflow:'hidden', position:'relative' }}>
                <img src={images[activeImg]} alt="car" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'}/>
                {images.length > 1 && (
                  <div style={{ position:'absolute', bottom:10, right:10, background:'rgba(0,0,0,0.65)', color:'#fff', fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>
                    {activeImg + 1}/{images.length}
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div style={{ display:'flex', gap:6, padding:'10px 12px', background:'rgba(0,31,63,0.96)', overflowX:'auto' }}>
                  {images.map((img, i) => (
                    <img key={i} src={img} alt="" onClick={() => setActiveImg(i)}
                      style={{ width:60, height:42, objectFit:'cover', borderRadius:5, cursor:'pointer', flexShrink:0, border:i===activeImg?'2px solid #FFD60A':'2px solid transparent', opacity:i===activeImg?1:0.55, transition:'all 0.15s' }}
                      onError={e=>e.target.style.display='none'}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ background:'linear-gradient(135deg,#001f3f,#003566)', borderRadius:16, height:200, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontSize:64, opacity:0.3 }}>🚗</span>
            </div>
          )}

          {/* Inspection Report */}
          {report && (
            <div style={{ background:'#fff', borderRadius:16, padding:'24px', boxShadow:'0 2px 12px rgba(0,53,102,0.08)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                <h2 style={{ margin:0, color:'#003566', fontSize:17, fontWeight:800 }}>📋 Inspection Report</h2>
                <span style={{ background:'#003566', color:'#FFD60A', fontSize:11, fontWeight:800, padding:'3px 12px', borderRadius:20 }}>{report.overall_score}/100</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:20, paddingBottom:20, borderBottom:'2px solid #f3f4f6' }}>
                <ScoreRing label="Overall"      score={report.overall_score}          max={100} />
                <ScoreRing label="Core Systems" score={report.core_systems_score}     max={10} />
                <ScoreRing label="Exterior"     score={report.exteriors_lights_score} max={10} />
                <ScoreRing label="Interior/AC"  score={report.interiors_ac_score}     max={10} />
                <ScoreRing label="Wear & Tear"  score={report.wear_tear_score}        max={10} />
              </div>
              <div style={{ marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                  <span style={{ fontSize:12, color:'#6b7280', fontWeight:600 }}>Supporting Systems</span>
                  <span style={{ fontSize:12, fontWeight:800, color:'#003566' }}>{report.supporting_systems_score}/10</span>
                </div>
                <div style={{ height:6, background:'#f3f4f6', borderRadius:10, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${report.supporting_systems_score * 10}%`, background:scoreColor(report.supporting_systems_score), borderRadius:10, transition:'width 0.6s' }}/>
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <span style={{ fontSize:12, fontWeight:700, padding:'5px 12px', borderRadius:20, background:report.meter_tampered?'#fee2e2':'#dcfce7', color:report.meter_tampered?'#991b1b':'#166534' }}>
                  {report.meter_tampered ? '⚠️ Meter Tampered' : '✅ Meter OK'}
                </span>
                <span style={{ fontSize:12, fontWeight:700, padding:'5px 12px', borderRadius:20, background:report.flooded?'#fee2e2':'#dcfce7', color:report.flooded?'#991b1b':'#166534' }}>
                  {report.flooded ? '⚠️ Flooded' : '✅ Not Flooded'}
                </span>
                {report.next_service_due && (
                  <span style={{ fontSize:12, fontWeight:700, padding:'5px 12px', borderRadius:20, background:'#dbeafe', color:'#1d4ed8' }}>
                    🔧 Service Due: {report.next_service_due}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Inspection Items */}
          {items.length > 0 && (
            <div style={{ background:'#fff', borderRadius:16, padding:'24px', boxShadow:'0 2px 12px rgba(0,53,102,0.08)' }}>
              <h2 style={{ margin:'0 0 20px', color:'#003566', fontSize:17, fontWeight:800 }}>
                🔍 Inspection Items
                <span style={{ marginLeft:10, fontSize:12, background:'#f3f4f6', color:'#6b7280', padding:'2px 10px', borderRadius:20, fontWeight:700 }}>{items.length} items</span>
              </h2>
              {Object.entries(itemsByCategory).map(([cat, catItems]) => (
                <div key={cat} style={{ marginBottom:18 }}>
                  <h3 style={{ margin:'0 0 8px', fontSize:11, fontWeight:800, color:'#003566', textTransform:'uppercase', letterSpacing:1.2, paddingBottom:6, borderBottom:'2px solid #f3f4f6' }}>{cat}</h3>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {catItems.map(item => {
                      const [bg, color] = itemStatusStyle(item.status);
                      return (
                        <div key={item.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:'#f8fafc', borderRadius:8, flexWrap:'wrap' }}>
                          <span style={{ flex:1, fontSize:13, color:'#374151', fontWeight:600 }}>{item.name}</span>
                          <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background:bg, color, textTransform:'capitalize', whiteSpace:'nowrap' }}>
                            {(item.status || 'unknown').replace(/_/g,' ')}
                          </span>
                          {item.remarks && <span style={{ width:'100%', fontSize:11, color:'#9ca3af', fontStyle:'italic' }}>"{item.remarks}"</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — Sticky */}
        <div style={{ position:'sticky', top:80, display:'flex', flexDirection:'column', gap:16 }}>
          {/* Car Details */}
          <div style={{ background:'#fff', borderRadius:16, padding:'20px', boxShadow:'0 2px 12px rgba(0,53,102,0.08)' }}>
            <h3 style={{ margin:'0 0 14px', color:'#003566', fontSize:14, fontWeight:800 }}>🚗 Car Details</h3>
            {[['Brand',car.brand],['Model',car.model],['Year',car.year],['City',car.city],['KM Driven',fmtKm(car.km_driven)]].filter(([,v])=>v).map(([k,v])=>(
              <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f3f4f6', fontSize:13 }}>
                <span style={{ color:'#6b7280' }}>{k}</span>
                <span style={{ fontWeight:700, color:'#003566' }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Price Info */}
          <div style={{ background:'#fff', borderRadius:16, padding:'20px', boxShadow:'0 2px 12px rgba(0,53,102,0.08)' }}>
            <h3 style={{ margin:'0 0 14px', color:'#003566', fontSize:14, fontWeight:800 }}>💰 Price Details</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              {[['Min',listing?.price_range_min],['Max',listing?.price_range_max]].map(([l,v])=>(
                <div key={l} style={{ background:'#f0f4f8', borderRadius:10, padding:'12px 14px' }}>
                  <p style={{ margin:'0 0 2px', fontSize:9, color:'#9ca3af', fontWeight:700, textTransform:'uppercase' }}>{l} Price</p>
                  <p style={{ margin:0, fontSize:14, fontWeight:800, color:'#003566' }}>{fmt(v)}</p>
                </div>
              ))}
            </div>
            <div style={{ background:'linear-gradient(135deg,#003566,#0057b7)', borderRadius:10, padding:'13px 16px' }}>
              <p style={{ margin:'0 0 2px', fontSize:9, color:'rgba(255,214,10,0.8)', fontWeight:700, textTransform:'uppercase' }}>Expected Price</p>
              <p style={{ margin:0, fontSize:18, fontWeight:800, color:'#FFD60A' }}>{fmt(listing?.expected_price)}</p>
            </div>
          </div>

          {/* Live Stats */}
          <div style={{ background:'#fff', borderRadius:16, padding:'20px', boxShadow:'0 2px 12px rgba(0,53,102,0.08)', animation:liveFlash?'flash 0.8s ease':'none', border:liveFlash?'2px solid #FFD60A':'2px solid transparent', transition:'border 0.3s' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
              <h3 style={{ margin:0, color:'#003566', fontSize:14, fontWeight:800 }}>🏆 Bidding</h3>
              {!isClosed && (
                <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, fontWeight:700, color:connected?'#16a34a':'#d97706' }}>
                  <span style={{ width:7, height:7, borderRadius:'50%', background:connected?'#16a34a':'#d97706', animation:connected?'pulse 1.5s infinite':'none', display:'inline-block' }}/>
                  {connected ? 'LIVE' : 'Connecting'}
                </span>
              )}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
              <div style={{ background:'linear-gradient(135deg,#FFD60A,#FFC107)', borderRadius:10, padding:'12px', textAlign:'center' }}>
                <p style={{ margin:'0 0 2px', fontSize:9, fontWeight:700, color:'#003566', textTransform:'uppercase' }}>Highest</p>
                <p style={{ margin:0, fontSize:16, fontWeight:800, color:'#003566' }}>{fmt(liveStats.highest)||'—'}</p>
              </div>
              <div style={{ background:'#f0f4f8', borderRadius:10, padding:'12px', textAlign:'center' }}>
                <p style={{ margin:'0 0 2px', fontSize:9, fontWeight:700, color:'#9ca3af', textTransform:'uppercase' }}>Total</p>
                <p style={{ margin:0, fontSize:16, fontWeight:800, color:'#003566' }}>{liveStats.total}</p>
              </div>
            </div>
            {!isClosed && listing?.bidding_end_at && (
              <div style={{ background:'#fef9c3', borderRadius:8, padding:'7px 12px', fontSize:11, color:'#854d0e', fontWeight:600, textAlign:'center' }}>
                Ends: {new Date(listing.bidding_end_at).toLocaleString('en-IN')}
              </div>
            )}
          </div>

          {/* ── BID FORM or NO PASS GATE or CLOSED ── */}
          {!isClosed ? (
            !passLoading && !hasPass ? (
              /* No pass — show gate */
              <div style={{ background:'linear-gradient(135deg,#1a0a00,#3d1a00)', borderRadius:16, padding:'22px', border:'2px solid #f59e0b', textAlign:'center' }}>
                <p style={{ fontSize:36, margin:'0 0 10px' }}>🔒</p>
                <p style={{ margin:'0 0 6px', fontWeight:800, color:'#FFD60A', fontSize:15 }}>Bidding Pass Required</p>
                <p style={{ margin:'0 0 16px', fontSize:12, color:'rgba(255,255,255,0.65)', lineHeight:1.5 }}>
                  Purchase a bidding pass to place unlimited bids during the pass period.
                </p>
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12, textAlign:'left' }}>
                  {[['⚡ 1 Day Pass','₹99','Unlimited bids for 1 day'],['🔥 Weekly Pass','₹299','Unlimited bids for 7 days'],['👑 Monthly Pass','₹799','Unlimited bids for 30 days']].map(([label, price, desc]) => (
                    <div key={label} style={{ background:'rgba(255,255,255,0.07)', borderRadius:8, padding:'10px 12px', border:'1px solid rgba(245,158,11,0.3)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <div>
                        <div style={{ color:'#FFD60A', fontWeight:700, fontSize:13 }}>{label}</div>
                        <div style={{ color:'rgba(255,255,255,0.5)', fontSize:11 }}>{desc}</div>
                      </div>
                      <div style={{ color:'#f59e0b', fontWeight:800, fontSize:15 }}>{price}</div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowPassModal(true)}
                  style={{ width:'100%', padding:'13px', background:'linear-gradient(135deg,#FFD60A,#f59e0b)', border:'none', borderRadius:10, color:'#003566', fontWeight:900, fontSize:15, cursor:'pointer', letterSpacing:0.3 }}
                >
                  🔨 Buy Bidding Pass
                </button>
              </div>
            ) : (
              /* Has pass — show bid form */
              <div style={{ background:'linear-gradient(135deg,#003566,#001d3d)', borderRadius:16, padding:'20px', border:'2px solid #FFD60A' }}>
                <h3 style={{ margin:'0 0 5px', color:'#FFD60A', fontSize:15, fontWeight:800 }}>🔨 Place Your Bid</h3>
                <p style={{ margin:'0 0 14px', color:'rgba(255,255,255,0.6)', fontSize:12 }}>Must exceed {fmt(liveStats.highest)||'no bids yet'}</p>
                {passLoading && (
                  <p style={{ color:'rgba(255,214,10,0.6)', fontSize:11, margin:'0 0 10px' }}>Checking pass status…</p>
                )}
                <input
                  type="number" placeholder="Enter amount (₹)" value={offerPrice}
                  onChange={e=>setOfferPrice(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&handlePlaceBid()}
                  min={liveStats.highest ? Number(liveStats.highest)+1 : 1}
                  style={{ width:'100%', padding:'12px 14px', borderRadius:10, border:'2px solid #FFD60A', background:'rgba(255,255,255,0.1)', color:'#fff', fontSize:16, fontWeight:700, outline:'none', marginBottom:10, boxSizing:'border-box' }}
                />
                <button
                  onClick={handlePlaceBid} disabled={placing}
                  style={{ width:'100%', padding:'13px', background:placing?'#6b7280':'linear-gradient(135deg,#FFD60A,#FFC107)', border:'none', borderRadius:10, color:'#003566', fontWeight:800, fontSize:15, cursor:placing?'not-allowed':'pointer', transition:'all 0.2s' }}
                >
                  {placing ? '⏳ Placing…' : '🔨 Place Bid'}
                </button>
              </div>
            )
          ) : (
            <div style={{ background:'#fee2e2', border:'2px solid #dc354550', borderRadius:16, padding:'22px', textAlign:'center' }}>
              <p style={{ fontSize:30, margin:'0 0 8px' }}>🔒</p>
              <p style={{ margin:'0 0 4px', fontWeight:800, color:'#991b1b', fontSize:14 }}>Bidding Closed</p>
              <p style={{ margin:0, fontSize:11, color:'#b91c1c' }}>
                {listing?.deal_closed_at
                  ? `Finalized: ${new Date(listing.deal_closed_at).toLocaleString('en-IN')}`
                  : 'The auction window has ended.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bidding Pass Modal */}
      {showPassModal && (
        <BiddingPassModal
          onClose={() => setShowPassModal(false)}
          onSuccess={() => {
            setShowPassModal(false);
            setHasPass(true);
          }}
        />
      )}
    </div>
  );
}